<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()

const colorMode = useColorMode()
const previousPreference = colorMode.preference
colorMode.forced = true
colorMode.preference = 'dark'

onBeforeUnmount(() => {
  colorMode.forced = false
  colorMode.preference = previousPreference
})

const statusCode = computed(() => props.error?.statusCode || 500)
const isNotFound = computed(() => statusCode.value === 404)
const isUnauthorized = computed(() => statusCode.value === 401 || statusCode.value === 403)

const heading = computed(() => {
  if (isNotFound.value) return 'Page not found'
  if (isUnauthorized.value) return 'Sign in required'
  return 'Something went wrong'
})

const message = computed(() => {
  if (isNotFound.value) return 'The page you’re looking for doesn’t exist or may have moved.'
  if (isUnauthorized.value) return 'You need to be signed in to view this page.'
  return props.error?.message || 'An unexpected error occurred. Please try again.'
})

const icon = computed(() => {
  if (isNotFound.value) return 'i-lucide-compass'
  if (isUnauthorized.value) return 'i-lucide-lock'
  return 'i-lucide-circle-alert'
})

useHead({
  title: computed(() => {
    if (isNotFound.value) return 'Not Found | Rhythmic'
    if (isUnauthorized.value) return 'Sign in required | Rhythmic'
    return 'Error | Rhythmic'
  }),
  meta: [
    {
      name: 'description',
      content: computed(() => heading.value),
    },
  ],
  bodyAttrs: {
    class:
      'bg-linear-to-b from-surface-200 to-surface-0 dark:bg-linear-to-b dark:from-neutral-800 dark:to-neutral-950 overflow-x-hidden overflow-y-hidden py-16',
  },
})

function handleGoHome() {
  clearError({ redirect: '/' })
}

function handleReload() {
  clearError()
  if (process.client) window.location.reload()
}
</script>

<template>
  <UError
    :error="props.error"
    :ui="{
      root: 'text-center flex flex-col items-center justify-center min-h-screen px-6',
      statusMessage: 'text-2xl font-semibold text-surface-900 dark:text-white mt-6',
      message: 'max-w-sm mx-auto mt-2 text-surface-600 dark:text-surface-300',
      links: 'mt-8 flex flex-col sm:flex-row justify-center gap-3 w-full max-w-sm'
    }"
  >
    <template #statusCode>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-400">
        <UIcon
          :name="icon"
          class="size-8"
        />
      </div>
    </template>

    <template #statusMessage>
      {{ heading }}
    </template>

    <template #message>
      <p>{{ message }}</p>
      <div v-if="props.error?.statusMessage" class="mt-6 text-xs text-surface-500 dark:text-surface-400">
        {{ statusCode }} • {{ props.error.statusMessage }}
      </div>
    </template>

    <template #links>
      <UButton
        v-if="!isNotFound && !isUnauthorized"
        label="Try again"
        color="neutral"
        variant="subtle"
        block
        class="sm:flex-1"
        @click="handleReload"
      >
        <template #leading>
          <UIcon name="i-lucide-rotate-cw" class="size-4" aria-hidden="true" />
        </template>
      </UButton>
      <UButton
        label="Go home"
        color="neutral"
        variant="subtle"
        block
        class="sm:flex-1"
        @click="handleGoHome"
      >
        <template #leading>
          <UIcon name="i-lucide-house" class="size-4" aria-hidden="true" />
        </template>
      </UButton>
    </template>
  </UError>
</template>
